/* ============================================================================
   GALLERY OVERLAY — Figma 1199:149241 (D 1206:28205 · M 1208:143296), with
   Banana Republic's zoom modal as the behaviour reference.

   Desktop: an inset modal over the glass scrim. The images stack vertically,
   each the modal's full height, and scroll freely; the thumbnail rail (Figma
   Product Thumbnail: Active = black border, Inactive = 60%) follows the
   scroll and jumps to an image. Clicking an image — or the zoom button —
   swaps in that image at the modal's full width to scroll through, cursor
   and button flipping to zoom-out, as BR does. Videos play inline, muted.
   Mobile: full-bleed slides that swipe, the pill pager, double-tap to zoom.
   ========================================================================== */

import { stagger, MO, state, play, $, $$, mqDesktop } from './mo.js';
import { createDrawer } from './drawers.js';
import { announce } from './ui.js';

const ICON_ZOOM = 'assets/figma/icon-zoom.svg';
const ICON_ZOOM_OUT = 'assets/figma/icon-zoom-out.svg';

export function initGalleryOverlay() {
  const root = $('[data-drawer="overlay"]');
  const drawer = createDrawer(root, { mode: 'takeover' });
  const panel = $('[data-drawer-panel]', root);
  const stage = $('[data-gov-stage]', root);
  const rail = $('[data-gov-rail]', root);
  const pager = $('[data-gov-pager]', root);
  const zoomBtn = $('[data-gov-zoom]', root);
  const zoomView = $('[data-gov-zoomview]', root);
  let media = [];
  let index = 0;
  let zoomed = false;
  let videoIO = null;

  const slideHTML = (m, i, n) => (m.type === 'video'
    ? `<div class="gov__slide gov__slide--video" data-gov-slide="${i}">
        <div class="gov__media">
          <video class="gov__video" src="${m.src}" muted loop playsinline preload="metadata" aria-label="${m.alt}"></video>
          <button class="gov__play" type="button" aria-label="Play video" data-gov-play>
            <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><rect width="24" height="24" rx="12" fill="#fff"/><path class="gov__play-icon" d="M15.82 12 8.18 16.36V7.64z" fill="#282828"/><g class="gov__pause-icon" fill="#282828"><rect x="8" y="7.5" width="2.6" height="9" rx=".4"/><rect x="13.4" y="7.5" width="2.6" height="9" rx=".4"/></g></svg>
          </button>
        </div>
      </div>`
    : `<div class="gov__slide" data-gov-slide="${i}">
        <div class="gov__media"><img class="gov__img" src="${m.src}" alt="${m.alt}, ${i + 1} of ${n}" decoding="async" draggable="false"${i > 1 ? ' loading="lazy"' : ''}></div>
      </div>`);

  const thumbHTML = (m, i, n) => `
    <button class="gov__thumb${m.type === 'video' ? ' gov__thumb--video' : ''}" type="button" data-gov-thumb="${i}"
      aria-label="${m.type === 'video' ? 'Video' : `Image ${i + 1} of ${n}`}">
      ${m.type === 'video'
        ? `<video src="${m.src}#t=1.5" muted playsinline preload="metadata" tabindex="-1" aria-hidden="true"></video><img class="gov__badge" src="assets/figma/icon-video-badge.svg" alt="" width="24" height="24">`
        : `<img src="${m.src}" alt="" loading="lazy" decoding="async" draggable="false">`}
    </button>`;

  function render(color, name) {
    const alt = color.imageAlt ?? `${name} in ${color.name}`;
    media = color.images.map((src, i) => ({ type: 'image', src, alt: color.alts?.[i] ?? alt })); // a kit names each plate
    if (color.video) media.push({ type: 'video', src: color.video, alt: `${alt}, product video` });
    const n = media.length;
    panel.toggleAttribute('data-single', n < 2); // one photo: no rail or pager to page through
    stage.innerHTML = media.map((m, i) => slideHTML(m, i, n)).join('');
    rail.innerHTML = media.map((m, i) => thumbHTML(m, i, n)).join('');
    pager.innerHTML = media.map((m) => `<span class="gov__dot${m.type === 'video' ? ' gov__dot--video' : ''}"></span>`).join('');
    watchVideos();
  }

  /* ---- position ------------------------------------------------------------ */
  const vertical = () => mqDesktop.matches;
  function current() {
    const s = stage.firstElementChild;
    if (!s) return 0;
    const step = vertical() ? s.offsetHeight : s.offsetWidth;
    const pos = vertical() ? stage.scrollTop : stage.scrollLeft;
    return Math.max(0, Math.min(media.length - 1, Math.round(pos / Math.max(1, step))));
  }
  function mark(i) {
    index = i;
    $$('[data-gov-thumb]', rail).forEach((t, k) => t.setAttribute('aria-current', String(k === i)));
    $$('.gov__dot', pager).forEach((d, k) => d.toggleAttribute('data-active', k === i));
    const t = rail.children[i];
    if (t && vertical()) t.scrollIntoView({ block: 'nearest', behavior: state.reduced ? 'auto' : 'smooth' });
  }
  function goTo(i, { instant = false } = {}) {
    const s = stage.children[Math.max(0, Math.min(media.length - 1, i))];
    if (!s) return;
    const behavior = instant || state.reduced ? 'instant' : 'smooth';
    if (vertical()) stage.scrollTo({ top: s.offsetTop, behavior });
    else stage.scrollTo({ left: s.offsetLeft, behavior });
    mark(Number(s.dataset.govSlide));
  }
  let raf = 0;
  stage.addEventListener('scroll', () => {
    if (!raf) raf = requestAnimationFrame(() => { raf = 0; const i = current(); if (i !== index) mark(i); });
  }, { passive: true });

  rail.addEventListener('click', (e) => {
    const t = e.target.closest('[data-gov-thumb]');
    if (t) goTo(Number(t.dataset.govThumb));
  });

  /* ---- video: plays muted while it's the image in view; Reduced waits --------- */
  function setPlaying(slide, on) {
    const v = $('video', slide);
    const b = $('[data-gov-play]', slide);
    if (!v) return;
    if (on) v.play().catch(() => {}); else v.pause();
    b.setAttribute('aria-label', on ? 'Pause video' : 'Play video');
    b.toggleAttribute('data-playing', on);
  }
  function watchVideos() {
    videoIO?.disconnect();
    videoIO = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (!drawer.isOpen) return;
      setPlaying(en.target, en.isIntersecting && en.intersectionRatio > 0.6 && !state.reduced);
    }), { root: stage, threshold: [0, 0.6] });
    $$('.gov__slide--video', stage).forEach((s) => videoIO.observe(s));
  }
  stage.addEventListener('click', (e) => {
    const b = e.target.closest('[data-gov-play]');
    if (!b) return;
    e.stopPropagation();
    const slide = b.closest('.gov__slide');
    setPlaying(slide, $('video', slide).paused);
  });

  /* ---- zoom (desktop): BR's full-width view ------------------------------- */
  async function zoomIn(i, point) {
    const m = media[i];
    if (!m || m.type !== 'image') return;
    zoomed = true;
    panel.setAttribute('data-zoomed', '');
    zoomBtn.setAttribute('aria-pressed', 'true');
    zoomBtn.setAttribute('aria-label', 'Zoom out');
    $('img', zoomBtn).src = ICON_ZOOM_OUT;
    zoomView.innerHTML = `<img class="gov__zoom-img" src="${m.src}" alt="${m.alt}, zoomed" draggable="false">`;
    zoomView.hidden = false;
    const img = $('img', zoomView);
    await img.decode?.().catch(() => {});
    /* keep the point that was clicked under the cursor */
    const relY = point ? point.relY : 0.5;
    const viewY = point ? point.viewY : zoomView.clientHeight / 2;
    zoomView.scrollTop = Math.max(0, relY * img.offsetHeight - viewY);
    if (state.reduced) play(zoomView, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard });
    else {
      play(zoomView, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eStandard });
      play(img, { scale: [0.92, 1] }, { duration: MO.slow, ease: MO.eEntrance });
    }
    announce('Zoomed in. Scroll to see the full image.');
  }
  async function zoomOut() {
    if (!zoomed) return;
    zoomed = false;
    panel.removeAttribute('data-zoomed');
    zoomBtn.setAttribute('aria-pressed', 'false');
    zoomBtn.setAttribute('aria-label', 'Zoom in');
    $('img', zoomBtn).src = ICON_ZOOM;
    await play(zoomView, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
    if (!zoomed) { zoomView.hidden = true; zoomView.innerHTML = ''; }
  }
  stage.addEventListener('click', (e) => {
    if (!vertical()) return;
    const img = e.target.closest('.gov__img');
    if (!img) return;
    const r = img.getBoundingClientRect();
    const pr = panel.getBoundingClientRect();
    zoomIn(Number(img.closest('[data-gov-slide]').dataset.govSlide),
      { relY: (e.clientY - r.top) / r.height, viewY: e.clientY - pr.top });
  });
  zoomView.addEventListener('click', zoomOut);
  zoomBtn.addEventListener('click', () => (zoomed ? zoomOut() : zoomIn(index)));

  /* ---- zoom (mobile): double-tap, then drag to look around ------------------ */
  let lastTap = 0;
  let tapZoom = null;
  stage.addEventListener('pointerup', (e) => {
    if (vertical() || e.pointerType !== 'touch') return;
    const img = e.target.closest('.gov__img');
    if (!img) return;
    const now = performance.now();
    if (now - lastTap > 300) { lastTap = now; return; }
    lastTap = 0;
    if (tapZoom) {
      tapZoom.style.transform = '';
      tapZoom = null;
      stage.removeAttribute('data-locked');
      return;
    }
    const r = img.getBoundingClientRect();
    img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
    img.style.transform = 'scale(2)';
    tapZoom = img;
    stage.setAttribute('data-locked', '');
  });
  stage.addEventListener('pointermove', (e) => {
    if (!tapZoom || e.pointerType !== 'touch') return;
    const r = tapZoom.parentElement.getBoundingClientRect();
    tapZoom.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
  });

  /* ---- keys: arrows step through; Escape leaves zoom before the overlay ------- */
  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && zoomed) { e.preventDefault(); e.stopPropagation(); zoomOut(); return; }
    if (zoomed || e.target.closest('video')) return;
    const d = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1, PageDown: 1, PageUp: -1 }[e.key];
    if (d) { e.preventDefault(); goTo(index + d); }
    else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
    else if (e.key === 'End') { e.preventDefault(); goTo(media.length - 1); }
  });

  root.addEventListener('drawer:close', () => {
    $$('.gov__slide--video', stage).forEach((s) => setPlaying(s, false));
    if (tapZoom) { tapZoom.style.transform = ''; tapZoom = null; stage.removeAttribute('data-locked'); }
    if (zoomed) { zoomed = false; panel.removeAttribute('data-zoomed'); zoomView.hidden = true; }
  });

  return {
    open(color, i, trigger, { name = '' } = {}) {
      render(color, name);
      zoomed = false;
      panel.removeAttribute('data-zoomed');
      zoomView.hidden = true;
      zoomBtn.setAttribute('aria-pressed', 'false');
      zoomBtn.setAttribute('aria-label', 'Zoom in');
      $('img', zoomBtn).src = ICON_ZOOM;
      drawer.open(trigger);
      requestAnimationFrame(() => goTo(i, { instant: true }));
      if (!state.reduced && vertical()) {
        play([...rail.children], { x: [-12, 0] },
          { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.12 }) });
      }
    },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
}
